import { ConnectedPosition, OverlayModule, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, inject, Input, input, OnInit, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SignedByPartial, UserController } from '@domain';
import {
    DialogConfirmationControl,
    RaButtonComponent,
    RaDialogConfirmationService,
    RaInputComponent,
    RaToastService,
} from '@shared/components';
import { DocumentContractController } from 'src/app/domain/controllers/document/document-contract.controller';
import { DocumentSignedStatusEnum } from 'src/app/domain/enums/documents/document-signed-status.enum';
import { RaBadgeComponent } from 'src/app/shared/components/ra-badge';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SignatureStatusPipe } from '../../../../../../../shared/pipes/signature-status.pipe';
import { EditDataService } from '../../../services/_edit-data.service';
import { SignerOptionsComponent } from './signer-options/signer-options.component';
import { SignatureFlowComponent } from './user-signature/user-signature.component';

@Component({
    selector: 'modal-contract-signers',
    templateUrl: 'modal-contract-signers.component.html',
    styleUrl: 'modal-contract-signers.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        OverlayModule,
        RaModalComponent,
        RaBadgeComponent,
        RaButtonComponent,
        SignatureStatusPipe,
        RaInputComponent,
        SignatureFlowComponent,
        RaSquareIconComponent,
        SignerOptionsComponent,
        TranslatePipe,
        RaIconComponent,
    ],
})
export class ModalContractSignersComponent implements OnInit {
    @Input() signers: SignedByPartial[] = [];
    public loading = input<boolean>(false);
    public close = output<void>();
    public showNewSigner = signal<boolean>(false);
    public loadingUpdate = signal<boolean>(false);
    public loadingPublish = signal<boolean>(false);
    public showSetUserSignature = signal<boolean>(false);
    public showConfirmation = signal<boolean>(false);
    public openMenuIndex = signal<number | null>(null);
    public signerToEdit: SignedByPartial;
    public formGroup: FormGroup;

    public readonly signerOptionsPositions: ConnectedPosition[] = [
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: 8 },
    ];

    public readonly scrollStrategy = inject(ScrollStrategyOptions).reposition();

    // * [Injects]
    public editDataService = inject(EditDataService);
    private _utilService = inject(UtilService);
    private _raToastService = inject(RaToastService);
    private _userController = inject(UserController);
    private _documentContractController = inject(DocumentContractController);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _translate = inject(TranslateService);

    ngOnInit(): void {
        this._initForm();
    }

    // Inicializa o formulário
    private _initForm(): void {
        this.formGroup = new FormGroup({
            name: new FormControl('', [Validators.required]),
            email: new FormControl('', [Validators.required, Validators.email]),
        });
    }

    public toggleOptions(index: number): void {
        this.openMenuIndex.set(this.openMenuIndex() === index ? null : index);
    }

    public closeOptions(): void {
        this.openMenuIndex.set(null);
    }

    public getBadgeType(status: DocumentSignedStatusEnum): any {
        switch (status) {
            case DocumentSignedStatusEnum.SIGNED:
                return 'primary';
            case DocumentSignedStatusEnum.WAITING_SEND_EMAIL:
            case DocumentSignedStatusEnum.NOT_SIGNED:
                return 'success';
        }
    }

    // Adiciona o email do novo assinante
    public saveSigner(): void {
        if (!this._utilService.isFormValid(this.formGroup)) return;

        // Cria o novo assinante
        const newSigner = new SignedByPartial();
        newSigner.name = this.formGroup.value.name;
        newSigner.email = this.formGroup.value.email;
        newSigner.status = DocumentSignedStatusEnum.WAITING_SEND_EMAIL;

        // Verifica se o email já existe na lista de assinantes
        if (this.signers.some(signer => signer.email === newSigner.email) && !this.signerToEdit) {
            this._raToastService.warning({
                title: this._translate.instant('document.edit.contract.emailExistsTitle'),
                message: this._translate.instant('document.edit.contract.emailExistsMessage'),
            });

            return;
        }

        // Atualiza o assinante
        if (!this.signerToEdit) {
            this.signers.push(newSigner);
        } else {
            this.signers[
                this.signers.findIndex(signer => signer.email === this.signerToEdit.email)
            ] = newSigner;
        }

        // Reseta o formulário
        this.formGroup.reset();

        // Atualiza a lista de assinantes
        this.loadingUpdate.set(true);
        this.updateList();
    }

    // Exibe um dialog de confirmação para remover o assinante
    public dialogConfirmRemove(index: number): void {
        this._raDialogConfirmationService
            .confirm({
                title: this._translate.instant('document.edit.contract.removeSignerTitle'),
                message: this._translate.instant('document.edit.contract.removeSignerMessage'),
                confirmText: this._translate.instant('document.edit.contract.removeSignerConfirm'),
                cancelText: this._translate.instant('common.cancel'),
                type: 'danger',
                irreversible: true,
            })
            .subscribe({
                next: (dialogControl: DialogConfirmationControl) => {
                    this.signers.splice(index, 1);
                    this.loadingUpdate.set(true);
                    this.updateList();

                    dialogControl.close();
                },
            });
    }

    // Edita o assinante
    public editSigner(index: number): void {
        this.signerToEdit = this.signers[index];

        this.formGroup.patchValue({
            name: this.signerToEdit.name,
            email: this.signerToEdit.email,
        });

        this.showNewSigner.set(true);
    }

    // Atualiza a lista de assinantes
    public updateList(dialogControl?: DialogConfirmationControl): void {
        this.loadingUpdate.set(true);
        this._documentContractController
            .updateSignedBy(this.editDataService.document()?._id, this.signers)
            .subscribe({
                next: () => {
                    this.loadingUpdate.set(false);

                    if (dialogControl) {
                        dialogControl.setLoading(false);
                        dialogControl.close();
                    }
                },
                error: () => {
                    this.loadingUpdate.set(false);

                    if (dialogControl) {
                        dialogControl.setLoading(false);
                        dialogControl.close();
                    }
                },
            });
    }

    // Envia para assinatura
    public sendToSignature(): void {
        // Garante pelo menos 2 assinantes antes de enviar
        if (this.signers.length < 2) {
            this._raToastService.warning({
                title: this._translate.instant('document.edit.contract.minSignersTitle'),
                message: this._translate.instant('document.edit.contract.minSignersMessage'),
            });
            return;
        }

        const user = this._userController.currentUser();
        if (!user.signatureConfig) {
            this.showSetUserSignature.set(true);
            return;
        }

        // Publica o contrato
        this.loadingPublish.set(true);
        this._documentContractController
            .publishContract(this.editDataService.document()?._id)
            .subscribe({
                next: () => this.close.emit(),
                error: () => this.loadingPublish.set(false),
            });
    }
}
